/**
 * Login Page (Web)
 * Email/password authentication using Firebase
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this component only handles UI.
 * Authentication logic is in useAuth hook (Firebase integration).
 */

import { useState, FormEvent, ChangeEvent, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  Paper,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { useAuth } from '@shared/features/auth';

type PricingPlanKey = 'freemium' | 'basic' | 'pro' | 'elite';

const getNavButtonStyles = (isActive = false) => ({
  textTransform: 'none',
  fontWeight: 500,
  color: isActive ? '#fff' : '#04377b',
  bgcolor: isActive ? '#04377b' : 'transparent',
  '&:hover': {
    bgcolor: isActive ? '#032e64' : 'rgba(4, 55, 123, 0.05)',
  },
});

// Waveform Dots Animation Component
function WaveformDots() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const timeRef = useRef(0);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetCenterRef = useRef({ x: 0, y: 0 });
  const currentCenterRef = useRef({ x: 0, y: 0 });
  const particlesRef = useRef<Array<{ x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number }>>([]);
  const textPositionRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
      // Calculate flowRithm text position (left side, upper-middle)
      textPositionRef.current = {
        x: canvas.width * 0.15, // Left side where flowRithm text is
        y: canvas.height * 0.35, // Upper-middle area
      };
      // Initialize center near the flowRithm text area
      if (currentCenterRef.current.x === 0 && currentCenterRef.current.y === 0) {
        currentCenterRef.current = { ...textPositionRef.current };
        targetCenterRef.current = { ...textPositionRef.current };
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Mouse tracking - listen on window to capture all movements
    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      // Update target center to follow mouse with some smoothing
      targetCenterRef.current.x = mouseRef.current.x;
      targetCenterRef.current.y = mouseRef.current.y;
    };

    window.addEventListener('mousemove', handleMouseMove);

    // Waveform configuration
    const dotSize = 4.5; // Increased dot size
    const dotSpacing = 18;
    const waveAmplitude = 35;
    const waveSpeed = 0.002;
    const waveFrequency = 0.01;
    const numWaves = 10;
    const followSpeed = 0.08; // Speed at which waveform follows mouse
    const colors = [
      'rgba(255, 193, 7, 0.5)',    // Amber gold - increased opacity
      'rgba(255, 215, 0, 0.45)',   // Gold
      'rgba(255, 223, 0, 0.4)',    // Golden yellow
      'rgba(255, 235, 59, 0.35)',  // Light golden
      'rgba(255, 248, 220, 0.3)',  // Light golden cream
      'rgba(255, 200, 0, 0.48)',   // Deep gold
      'rgba(255, 220, 50, 0.42)',  // Bright gold
      'rgba(255, 230, 80, 0.38)',  // Pale gold
      'rgba(255, 240, 100, 0.33)', // Soft gold
      'rgba(255, 250, 180, 0.28)', // Cream gold
    ];

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      timeRef.current += 1;

      // Smoothly follow the mouse position
      currentCenterRef.current.x += (targetCenterRef.current.x - currentCenterRef.current.x) * followSpeed;
      currentCenterRef.current.y += (targetCenterRef.current.y - currentCenterRef.current.y) * followSpeed;

      const centerX = currentCenterRef.current.x;
      const centerY = currentCenterRef.current.y;
      const mouseX = mouseRef.current.x;
      const mouseY = mouseRef.current.y;
      const textX = textPositionRef.current.x;
      const textY = textPositionRef.current.y;

      // Particle system: dots from flowRithm text toward mouse
      const particles = particlesRef.current;
      const particleSpawnRate = 2; // Spawn particles every N frames
      
      // Spawn new particles from text position
      if (timeRef.current % particleSpawnRate === 0) {
        const distanceToMouse = Math.sqrt((mouseX - textX) ** 2 + (mouseY - textY) ** 2);
        if (distanceToMouse > 50) { // Only spawn if mouse is far enough
          const angle = Math.atan2(mouseY - textY, mouseX - textX);
          const speed = 2 + Math.random() * 2;
          const spread = (Math.random() - 0.5) * 0.3; // Slight spread
          particles.push({
            x: textX + (Math.random() - 0.5) * 30, // Small random offset from text
            y: textY + (Math.random() - 0.5) * 20,
            vx: Math.cos(angle + spread) * speed,
            vy: Math.sin(angle + spread) * speed,
            life: 0,
            maxLife: 200 + Math.random() * 100,
            size: 3 + Math.random() * 2,
          });
        }
      }

      // Update and draw particles
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life++;

        // Remove particles that are too old or have reached mouse
        const distToMouse = Math.sqrt((mouseX - p.x) ** 2 + (mouseY - p.y) ** 2);
        if (p.life > p.maxLife || distToMouse < 20) {
          particles.splice(i, 1);
          continue;
        }

        // Draw particle
        const alpha = 1 - (p.life / p.maxLife);
        ctx.fillStyle = `rgba(255, 215, 0, ${alpha * 0.7})`; // Gold color
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      // Synchronized wave phase and speed for all layers
      const synchronizedPhase = timeRef.current * waveSpeed;
      
      // Draw multiple waveform layers - all in sync
      for (let waveIndex = 0; waveIndex < numWaves; waveIndex++) {
        // All waves use the same phase and speed for synchronization
        const amplitude = waveAmplitude * (1 - waveIndex * 0.12);
        const color = colors[waveIndex];

        ctx.fillStyle = color;

        // Draw dots along the waveform across the entire screen width
        const startX = 0;
        const endX = canvas.width;

        for (let x = startX; x < endX; x += dotSpacing) {
          // Calculate distance from mouse to influence wave
          const dx = mouseX - x;
          const dy = mouseY - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = 250; // Influence radius
          const influence = Math.max(0, 1 - distance / maxDistance);
          
          // Mouse influence on wave phase and amplitude - same for all waves
          const mousePhase = influence * Math.sin(distance * 0.04 + timeRef.current * 0.01) * 0.8;
          const mouseAmplitude = amplitude * (1 + influence * 0.6);
          
          // Waveform centered around the following position - all waves use same phase
          const relativeX = x - centerX;
          const y =
            centerY +
            mouseAmplitude *
              Math.sin(
                relativeX * waveFrequency +
                  synchronizedPhase +
                  mousePhase
              ) +
            (waveIndex - 4.5) * 20; // Vertical offset for each wave (centered for 10 waves)

          // Only draw if within canvas bounds
          if (y >= 0 && y <= canvas.height) {
            // Vary dot size based on mouse proximity and distance from center
            const distanceFromCenter = Math.abs(relativeX);
            const centerInfluence = Math.max(0, 1 - distanceFromCenter / (canvas.width * 0.5));
            const dotSizeVariation = dotSize * (1 + influence * 0.8 + centerInfluence * 0.3);
            ctx.beginPath();
            ctx.arc(x, y, dotSizeVariation, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
        opacity: 0.6,
      }}
    />
  );
}

export function LoginPage() {
  const { login, register, signInWithGoogle, sendMobileOTP, verifyMobileOTP, sendEmailVerificationLink, resetPassword, loading, error } = useAuth();
  const navigate = useNavigate();
  // Sign-in form state
  const [signInEmail, setSignInEmail] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  // Sign-up form state
  const [signUpInput, setSignUpInput] = useState(''); // Can be mobile or email
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  // Card visibility state - default to signin
  const [showSignInCard, setShowSignInCard] = useState(true);
  // Forgot password state
  const [showForgotPasswordCard, setShowForgotPasswordCard] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  // Legacy state for modal (keeping for backward compatibility)
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState<'login' | 'signup' | null>(null);
  const [showFeatures, setShowFeatures] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [showAboutUs, setShowAboutUs] = useState(false);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const currentYear = new Date().getFullYear();
  const heroFeatures = [
    {
      title: 'AI Screen & Strategy Builder',
      description: 'Design screeners and strategies with AI and natural language—no coding needed.',
      icon: '🤖',
    },
    {
      title: 'Parallel Order-Execution',
      description: 'Place multiple orders instantly across assets and multiple brokers.',
      icon: '⚡',
    },
    {
      title: 'Advanced Testing Suite',
      description: 'Backtest and forward test your strategies to validate and refine, risk-free.',
      icon: '🧪',
    },
    {
      title: 'Trade-Hub',
      description: 'Order, track, and manage live trades from one intuitive dashboard.',
      icon: '📟',
    },
    {
      title: 'AI-Powered Analytics',
      description: 'Gain instant insights from market data with intelligent analytics.',
      icon: '📊',
    },
    {
      title: 'Scalability & Compliance',
      description: 'Scale up confidently with enterprise-grade controls and guardrails.',
      icon: '🛡️',
    },
  ];

  // Sign-in handlers
  const handleSignInEmailChange = (event: ChangeEvent<HTMLInputElement>) => setSignInEmail(event.target.value);
  const handleSignInPasswordChange = (event: ChangeEvent<HTMLInputElement>) => setSignInPassword(event.target.value);
  const handleRememberMeChange = (event: ChangeEvent<HTMLInputElement>) => setRememberMe(event.target.checked);
  // Sign-up handlers
  const handleSignUpInputChange = (event: ChangeEvent<HTMLInputElement>) => setSignUpInput(event.target.value);
  const handleOtpChange = (event: ChangeEvent<HTMLInputElement>) => setOtp(event.target.value);
  
  // Helper function to detect if input is mobile or email
  const isMobileNumber = (input: string): boolean => {
    // Check if input contains only digits and possibly +, spaces, dashes
    const mobilePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    return mobilePattern.test(input.replace(/\s/g, '')) && input.replace(/\D/g, '').length >= 10;
  };
  // Legacy handlers (keeping for backward compatibility)
  const handleMobileChange = (event: ChangeEvent<HTMLInputElement>) => setMobileNumber(event.target.value);
  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value);
  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value);
  const handleContactEmailChange = (event: ChangeEvent<HTMLInputElement>) => setContactEmail(event.target.value);
  const handleContactMessageChange = (event: ChangeEvent<HTMLInputElement>) => setContactMessage(event.target.value);
  const handleForgotPassword = () => {
    setShowForgotPasswordCard(true);
    setShowSignInCard(false);
  };
  
  const handleForgotPasswordEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForgotPasswordEmail(event.target.value);
  };
  
  const handleForgotPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await resetPassword(forgotPasswordEmail);
      setPasswordResetSent(true);
    } catch (err) {
      // Error handled by useAuth hook
    }
  };
  
  const handleBackToSignIn = () => {
    setShowForgotPasswordCard(false);
    setShowSignInCard(true);
    setForgotPasswordEmail('');
    setPasswordResetSent(false);
  };
  const handleFeaturesClick = () => {
    setShowPricing(false);
    setShowContact(false);
    setShowDemo(false);
    setShowAboutUs(false);
    setAuthMode(null);
    setShowFeatures((prev) => !prev);
  };
  const handlePricingToggle = () => {
    setShowFeatures(false);
    setShowContact(false);
    setShowDemo(false);
    setShowAboutUs(false);
    setShowPricing((prev) => !prev);
  };
  const handleContactToggle = () => {
    setShowFeatures(false);
    setShowPricing(false);
    setShowDemo(false);
    setShowAboutUs(false);
    setAuthMode(null);
    setShowContact((prev) => !prev);
    // Reset contact form when closing
    if (showContact) {
      setContactSubmitted(false);
      setContactEmail('');
      setContactMessage('');
    }
  };

  const handleContactSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!contactEmail.trim() || !contactMessage.trim()) {
      return;
    }

    setContactSubmitting(true);
    
    try {
      // TODO: Integrate with contact API endpoint when available
      // For now, simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      
      setContactSubmitted(true);
      setContactEmail('');
      setContactMessage('');
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setContactSubmitted(false);
        setShowContact(false);
      }, 5000);
    } catch (err) {
      // Handle error - could show error message
      console.error('Failed to send contact message:', err);
    } finally {
      setContactSubmitting(false);
    }
  };
  const handleDemoToggle = () => {
    setShowFeatures(false);
    setShowPricing(false);
    setShowContact(false);
    setShowAboutUs(false);
    setAuthMode(null);
    setShowDemo((prev) => !prev);
  };
  const handleAboutUsToggle = () => {
    setShowFeatures(false);
    setShowPricing(false);
    setShowContact(false);
    setShowDemo(false);
    setAuthMode(null);
    setShowAboutUs((prev) => !prev);
  };
  const handleAuthOpen = (mode: 'login' | 'signup') => {
    setShowFeatures(false);
    setShowPricing(false);
    setShowContact(false);
    setShowDemo(false);
    setShowAboutUs(false);
    setAuthMode(mode);
  };


  const pricingPlans: Record<
    PricingPlanKey,
    { name: string; price: string; features: string[] }
  > = {
    freemium: {
      name: 'Freemium',
      price: '₹0 / month',
      features: [
        '1 broker connection',
        '2 templates & limited AI assistance',
        '2 strategies, 1 backtest per week',
      ],
    },
    basic: {
      name: 'Basic',
      price: '₹499 / month',
      features: [
        '3 brokers + 5 screeners',
        'Contextual help, 5 strategies',
        '5 backtests per day',
      ],
    },
    pro: {
      name: 'Pro',
      price: '₹999 - ₹1,999 / month',
      features: [
        '10 brokers + 10 screeners',
        'Full AI assistant & optimizer',
        'Behavioural analytics, 10 strategies',
      ],
    },
    elite: {
      name: 'Elite',
      price: '₹3,999 - ₹9,999+ / month',
      features: [
        '30 brokers + 50 screeners',
        'Agentic AI, PMS-grade multi-client',
        'White-label & premium support',
      ],
    },
  };

  // Sign-in form submit
  const handleSignInSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await login(signInEmail, signInPassword, rememberMe);
      navigate('/ai-assist');
    } catch (err) {
      // Error handled by useAuth hook
    }
  };

  // Sign-up form submit - handles mobile OTP or email verification
  const handleSignUpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!signUpInput.trim()) {
      return;
    }

    const input = signUpInput.trim();
    
    if (isMobileNumber(input)) {
      // Mobile number flow - send OTP
      try {
        const verificationId = await sendMobileOTP(input);
        setVerificationId(verificationId);
        setOtpSent(true);
      } catch (err) {
        // Error handled by useAuth hook
      }
    } else {
      // Email flow - send verification link
      try {
        await sendEmailVerificationLink(input);
        setEmailVerificationSent(true);
      } catch (err) {
        // Error handled by useAuth hook
      }
    }
  };

  // OTP verification submit
  const handleOtpSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!otp || !verificationId) {
      return;
    }
    try {
      await verifyMobileOTP(verificationId, otp);
      navigate('/ai-assist');
    } catch (err) {
      // Error handled by useAuth hook
    }
  };

  // Legacy submit handler (keeping for backward compatibility)
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (authMode === 'signup') {
        await register(email, password);
      } else {
        await login(email, password, rememberMe);
      }
      navigate('/ai-assist');
    } catch (err) {
      // Error handled by useAuth hook
    }
  };

  return (
    <Box
      sx={{
        minHeight: '120vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        overflowX: 'hidden',
      }}
    >
      {/* Beta Banner */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          backgroundColor: '#ff9800',
          color: '#fff',
          py: 1.5,
          px: { xs: 2, md: 4 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: { xs: '0.875rem', md: '0.9375rem' },
            textAlign: 'center',
            letterSpacing: '0.02em',
          }}
        >
          Beta Version
        </Typography>
      </Box>

      <Box
        component="header"
        sx={{
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
          border: '1px solid',
          borderColor: 'rgba(4, 55, 123, 0.3)',
          borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          py: 2,
          px: { xs: 3, md: 6 },
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          backdropFilter: 'blur(10px)',
          boxShadow: `
            0 0 0 1px rgba(4, 55, 123, 0.15),
            0 0 8px rgba(4, 55, 123, 0.2),
            0 0 15px rgba(4, 55, 123, 0.15),
            0 3px 16px rgba(4, 55, 123, 0.25),
            0 1px 6px rgba(4, 55, 123, 0.15),
            inset 0 0 10px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(4, 55, 123, 0.1)
          `,
          position: 'fixed',
          top: { xs: '48px', md: '48px' },
          left: 0,
          right: 0,
          zIndex: 1100,
          height: '64px',
          minHeight: '64px',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          width: '100%',
        }}
      >
        <Box
          component="img"
          src="/assets/flowrithm-logo.png"
          alt="FlowRithm logo"
          sx={{ height: 32 }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.25, md: 2 },
            flex: 1,
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            justifyContent: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Button
            variant={showFeatures ? 'contained' : 'text'}
            color="inherit"
            sx={getNavButtonStyles(showFeatures)}
            onClick={handleFeaturesClick}
          >
            Features
          </Button>
          <Button
            variant={showPricing ? 'contained' : 'text'}
            color="inherit"
            sx={getNavButtonStyles(showPricing)}
            onClick={handlePricingToggle}
          >
            Pricing
          </Button>
          <Button
            variant={showContact ? 'contained' : 'text'}
            color="inherit"
            sx={getNavButtonStyles(showContact)}
            onClick={handleContactToggle}
          >
            Contact
          </Button>
          <Button
            variant={showAboutUs ? 'contained' : 'text'}
            color="inherit"
            sx={getNavButtonStyles(showAboutUs)}
            onClick={handleAboutUsToggle}
          >
            About Us
          </Button>
          <Button
            variant={showDemo ? 'contained' : 'text'}
            color="inherit"
            sx={getNavButtonStyles(showDemo)}
            onClick={handleDemoToggle}
          >
            Product Preview
          </Button>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          position: 'relative',
          minHeight: { xs: 'auto', md: '100vh' },
          backgroundImage: 'url(/assets/login-hero.png)',
          backgroundSize: 'cover',
          backgroundPosition: { xs: 'center top', md: 'left center' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 10 },
          py: { xs: 6, md: 8 },
          pb: { xs: 10, md: 20 },
          pt: { xs: 18, md: 18 }, // Add top padding to account for fixed banner and header
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.6) 40%, rgba(255,255,255,0.2) 65%, rgba(255,255,255,0) 100%)',
            zIndex: 0,
          },
        }}
      >
        {/* Animated Waveform Dots */}
        <WaveformDots />
        <Box
          sx={{
            width: '100%',
            maxWidth: 1280,
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 4, md: 6 },
            alignItems: { xs: 'flex-start', md: 'flex-start' },
            justifyContent: { xs: 'flex-start', md: 'space-between' },
          }}
        >
          {/* Main Content Section - Center - Always visible */}
          <Box sx={{ flex: 1, maxWidth: { xs: '100%', md: '600px' }, mx: { xs: 0, md: 'auto' }, ml: { xs: 0, md: 4 } }}>
              {/* Title */}
              <Typography
                variant="h3"
            sx={{
                  fontWeight: 700,
                  color: '#04377b',
                  fontSize: { xs: '2rem', md: '2.75rem' },
                  mb: 0.5,
                  lineHeight: 1.2,
                }}
              >
                flowRithm
              </Typography>

              {/* Subtitle */}
              <Typography
                variant="body1"
                sx={{
                  color: '#212121',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  mb: 4,
                  lineHeight: 1.6,
                  maxWidth: '600px',
                  fontWeight: 500,
                }}
              >
                Simple. Smart. Secure.
              </Typography>

              {/* Key Metrics */}
              <Box
                sx={{
              display: 'flex',
                  gap: { xs: 3, md: 6 },
                  mb: 5,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                      fontWeight: 700,
                      color: '#212121',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      mb: 0.5,
                    }}
                  >
                    -
              </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.875rem' }}>
                    Active Traders
                  </Typography>
                </Box>
                <Box>
              <Typography
                    variant="h4"
                sx={{
                  fontWeight: 700,
                      color: '#212121',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      mb: 0.5,
                    }}
                  >
                    -
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.875rem' }}>
                    Assets Under Management
              </Typography>
            </Box>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: '#212121',
                      fontSize: { xs: '1.5rem', md: '2rem' },
                      mb: 0.5,
                    }}
                  >
                    -
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#757575', fontSize: '0.875rem' }}>
                    Uptime
                  </Typography>
                </Box>
              </Box>

              {/* Why traders choose us */}
              <Box sx={{ mb: 5 }}>
                 <Box
                   sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     gap: 3,
                   }}
                 >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        border: '2px solid #04377b',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box
                        sx={{
                          width: 18,
                          height: 18,
                          backgroundColor: '#04377b',
                          borderRadius: 0.5,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212121', mb: 0.5 }}>
                        Agentic-First Architecture
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
                        Advanced trading intelligence powered by AI Agents
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        border: '2px solid #04377b',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box
                        sx={{
                          width: 15,
                          height: 15,
                          border: '2px solid #04377b',
                          borderTop: 'none',
                          borderRight: 'none',
                          transform: 'rotate(-45deg)',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212121', mb: 0.5 }}>
                        Real-Time Execution
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
                        Lightning-fast order execution with minimal latency
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        border: '2px solid #04377b',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundColor: '#fff',
                      }}
                    >
                      <Box
                        sx={{
                          width: 15,
                          height: 15,
                          border: '2px solid #04377b',
                          borderRadius: '50% 50% 50% 0',
                          transform: 'rotate(-45deg)',
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#212121', mb: 0.5 }}>
                        Bank-Level Security
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#616161', fontSize: '0.875rem' }}>
                        Enterprise-grade security for your trading data
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Trusted by Professionals */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: '#9E9E9E',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  mb: 2,
                    display: 'block',
                    fontSize: '0.75rem',
                  }}
                >
                  TRUSTED BY PROFESSIONALS
              </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    color: '#212121',
                    fontSize: '0.875rem',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    SOC 2 Certified
            </Box>
                  <Box component="span" sx={{ color: '#9E9E9E', mx: 0.5 }}>
                    |
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    Bank-level Security
                  </Box>
                  <Box component="span" sx={{ color: '#9E9E9E', mx: 0.5 }}>
                    |
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: '#4CAF50',
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    24/7 Support
                  </Box>
                </Box>
               </Box>
          </Box>

          {/* Sign-up, Sign-in, and Forgot Password Cards - Right Side */}
          {!showFeatures && !showPricing && !showContact && !showDemo && !showAboutUs && !authMode && (
            <Box
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 420 },
                flexShrink: 0,
                position: { xs: 'relative', md: 'sticky' },
                top: { xs: 'auto', md: 80 },
                alignSelf: { xs: 'stretch', md: 'flex-start' },
                ml: { xs: 0, md: 'auto' },
                mr: { xs: 0, md: 0 },
                zIndex: 2,
              }}
            >
              {/* Forgot Password Card */}
              {showForgotPasswordCard && (
            <Paper
              elevation={0}
              sx={{
              width: '100%',
              p: { xs: 3, md: 4 },
              borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                    backdropFilter: 'blur(16px)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 70px rgba(10,37,64,0.4)',
                    },
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                    Reset Password
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0A2540', mb: 3, opacity: 0.8 }}>
                    Enter your email address and we'll send you a link to reset your password.
                  </Typography>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {error}
                    </Alert>
                  )}

                  {passwordResetSent ? (
                    <Box>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        Password reset email sent! Please check your inbox and follow the instructions.
                      </Alert>
                      <Button
                        variant="text"
                        onClick={handleBackToSignIn}
                        sx={{ textTransform: 'none', width: '100%' }}
                      >
                        Back to Sign In
                      </Button>
                    </Box>
                  ) : (
                    <Stack component="form" onSubmit={handleForgotPasswordSubmit} spacing={2}>
                      <TextField
                        type="email"
                        value={forgotPasswordEmail}
                        onChange={handleForgotPasswordEmailChange}
                        fullWidth
                        required
                        variant="filled"
                        placeholder="Email ID *"
                        InputProps={{
                          disableUnderline: true,
                          sx: {
                            backgroundColor: '#f0f0f0',
                            borderRadius: 2,
                            '& input': { py: 1.25 },
                          },
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        disabled={loading || !forgotPasswordEmail.trim()}
                        size="medium"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                      >
                        {loading ? 'Sending...' : 'Send Reset Link'}
                      </Button>
                      <Button
                        variant="text"
                        onClick={handleBackToSignIn}
                        sx={{ textTransform: 'none', width: '100%' }}
                      >
                        Back to Sign In
                      </Button>
                    </Stack>
                  )}
                </Paper>
              )}

              {/* Sign-up Card */}
              {!showSignInCard && !showForgotPasswordCard && (
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                    backdropFilter: 'blur(16px)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 70px rgba(10,37,64,0.4)',
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                  Create Account
                </Typography>
                <Typography variant="body2" sx={{ color: '#0A2540', mb: 3, opacity: 0.8 }}>
                  Sign up with mobile, email, or Google
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                {emailVerificationSent ? (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Verification email sent! Please check your inbox and click the link to set your password.
                  </Alert>
                ) : otpSent ? (
                  <Stack component="form" onSubmit={handleOtpSubmit} spacing={2}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      OTP sent to {signUpInput}. Please enter the verification code.
                    </Alert>
                    <TextField
                      type="text"
                      value={otp}
                      onChange={handleOtpChange}
                      variant="filled"
                      fullWidth
                      required
                      placeholder="Enter OTP"
                      inputProps={{ maxLength: 6 }}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          backgroundColor: '#f0f0f0',
                          borderRadius: 2,
                          '& input': { py: 1.25, textAlign: 'center', letterSpacing: '0.2em', fontSize: '1.2rem' },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading || !otp}
                      size="medium"
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      {loading ? 'Verifying...' : 'Verify OTP'}
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => {
                        setOtpSent(false);
                        setOtp('');
                        setVerificationId(null);
                      }}
                      sx={{ textTransform: 'none' }}
                    >
                      Change number
                    </Button>
                  </Stack>
                ) : (
                  <Stack component="form" onSubmit={handleSignUpSubmit} spacing={2}>
                    <TextField
                      type="text"
                      value={signUpInput}
                      onChange={handleSignUpInputChange}
                      variant="filled"
                      fullWidth
                      required
                      placeholder="Mobile number or Email ID"
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          backgroundColor: '#f0f0f0',
                          borderRadius: 2,
                          '& input': { py: 1.25 },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={loading || !signUpInput.trim()}
                      size="medium"
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      {loading ? 'Sending...' : 'Continue'}
                    </Button>
                    <Divider sx={{ my: 1 }}>or</Divider>
                    <Button
                      variant="contained"
                      color="inherit"
                      fullWidth
                      size="medium"
                      disabled={loading}
                      onClick={async () => {
                        try {
                          await signInWithGoogle();
                          navigate('/ai-assist');
                        } catch (err) {
                          // Error handled by useAuth hook
                        }
                      }}
                sx={{
                        backgroundColor: 'rgba(255,255,255,0.85)',
                        color: '#212121',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.1)',
                        textTransform: 'none',
                        fontWeight: 500,
                  display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        '&:hover': { 
                          backgroundColor: 'rgba(255,255,255,0.95)',
                        },
                      }}
                      startIcon={
                        <Box
                          component="img"
                          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E"
                          alt="Google"
                          sx={{ width: 18, height: 18 }}
                        />
                      }
                    >
                      Continue with Google
                    </Button>
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                      <Typography variant="body2" sx={{ color: '#0A2540' }}>
                        Registered user?{' '}
                        <Button
                          variant="text"
                          onClick={() => setShowSignInCard(true)}
                sx={{
                            textTransform: 'none',
                            padding: 0,
                            minWidth: 'auto',
                            fontWeight: 600,
                            color: '#04377b',
                            '&:hover': {
                              textDecoration: 'underline',
                              backgroundColor: 'transparent',
                  },
                }}
              >
                          Sign in
                        </Button>
                      </Typography>
              </Box>
                  </Stack>
                )}
              </Paper>
              )}

              {/* Sign-in Card */}
              {showSignInCard && !showForgotPasswordCard && (
                <Paper
                  elevation={0}
                sx={{
                    width: '100%',
                    p: { xs: 3, md: 4 },
                    borderRadius: 4,
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                    backdropFilter: 'blur(16px)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 24px 70px rgba(10,37,64,0.4)',
                  },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A2540', mb: 1 }}>
                  Sign In
                </Typography>
                <Typography variant="body2" sx={{ color: '#0A2540', mb: 3, opacity: 0.8 }}>
                  Welcome back! Continue to your account
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Stack component="form" onSubmit={handleSignInSubmit} spacing={2}>
                  <TextField
                    type="email"
                    value={signInEmail}
                    onChange={handleSignInEmailChange}
                    fullWidth
                    required
                    variant="filled"
                    placeholder="Email ID *"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        '& input': { py: 1.25 },
                      },
                    }}
                  />
                  <TextField
                    type="password"
                    value={signInPassword}
                    onChange={handleSignInPasswordChange}
                    fullWidth
                    required
                    variant="filled"
                    placeholder="Password *"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        '& input': { py: 1.25 },
                      },
                    }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <FormControlLabel
                      control={
                        <Checkbox checked={rememberMe} onChange={handleRememberMeChange} color="primary" size="small" />
                      }
                      label={<Typography variant="caption">Remember me</Typography>}
                    />
                    <Button
                      variant="text"
                      onClick={handleForgotPassword}
                      sx={{ textTransform: 'none', padding: 0, minWidth: 'auto' }}
                    >
                      <Typography variant="caption" color="primary">
                        Forgot password?
                      </Typography>
                    </Button>
              </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    size="medium"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  <Divider sx={{ my: 1 }}>or</Divider>
                  <Button
                    variant="contained"
                    color="inherit"
                    fullWidth
                    size="medium"
                    disabled={loading}
                    onClick={async () => {
                      try {
                        await signInWithGoogle();
                        navigate('/ai-assist');
                      } catch (err) {
                        // Error handled by useAuth hook
                      }
                    }}
                sx={{
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      color: '#212121',
                      borderRadius: 2,
                      border: '1px solid rgba(0,0,0,0.1)',
                      textTransform: 'none',
                      fontWeight: 500,
                  display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      '&:hover': { 
                        backgroundColor: 'rgba(255,255,255,0.95)',
                      },
                    }}
                    startIcon={
                      <Box
                        component="img"
                        src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E"
                        alt="Google"
                        sx={{ width: 18, height: 18 }}
                      />
                    }
                  >
                    Continue with Google
                  </Button>
                  <Box sx={{ textAlign: 'center', mt: 2 }}>
                    <Typography variant="body2" sx={{ color: '#0A2540' }}>
                      New user?{' '}
                      <Button
                        variant="text"
                        onClick={() => setShowSignInCard(false)}
                sx={{
                          textTransform: 'none',
                          padding: 0,
                          minWidth: 'auto',
                          fontWeight: 600,
                          color: '#04377b',
                          '&:hover': {
                            textDecoration: 'underline',
                            backgroundColor: 'transparent',
                  },
                }}
              >
                        Sign up
                      </Button>
                    </Typography>
              </Box>
                </Stack>
          </Paper>
              )}
            </Box>
          )}

          {showFeatures && (
            <Paper
              id="features-section"
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 900 },
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(16px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -250 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.5, color: '#0A2540' }}>
                  Features
                </Typography>
                <IconButton size="small" onClick={() => setShowFeatures(false)} sx={{ color: '#0A2540' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                  gap: 1.5,
                  gridAutoRows: 'minmax(auto, 1fr)',
                }}
              >
                {heroFeatures.map((feature) => (
                  <Box
                    key={feature.title}
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      backgroundColor: 'rgba(255,255,255,0.5)',
                      border: '2px solid rgba(25, 118, 210, 0.4)',
                      boxShadow: '0 0 15px rgba(25, 118, 210, 0.3), 0 4px 12px rgba(25, 118, 210, 0.2)',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        border: '2px solid rgba(25, 118, 210, 0.6)',
                        boxShadow: '0 0 25px rgba(25, 118, 210, 0.5), 0 8px 24px rgba(25, 118, 210, 0.3)',
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: '1.5rem', lineHeight: 1 }}>{feature.icon}</Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2540', fontSize: '0.95rem', lineHeight: 1.3 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0A2540', fontSize: '0.85rem', lineHeight: 1.4 }}>
                      {feature.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}

          {showContact && (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 900 },
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(16px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -250 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.5, color: '#0A2540' }}>
                  Contact FlowRithm
                </Typography>
                <IconButton size="small" onClick={() => setShowContact(false)} sx={{ color: '#0A2540' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              {contactSubmitted ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thank you for your message! We'll get back to you soon.
                </Alert>
              ) : (
                <>
              <Typography variant="body2" sx={{ color: '#0A2540', mb: 3 }}>
                    Share your email and message, and our team will reach out to you.
              </Typography>
                  <Stack component="form" onSubmit={handleContactSubmit} spacing={2}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, display: 'block' }}>
                    Email Address
                  </Typography>
                  <TextField
                  type="email"
                  value={contactEmail}
                  onChange={handleContactEmailChange}
                  variant="filled"
                  fullWidth
                      required
                      disabled={contactSubmitting}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      backgroundColor: '#f0f0f0',
                      borderRadius: 2,
                      '& input': { py: 1.5 },
                    },
                  }}
                    placeholder="you@example.com"
                  />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, display: 'block' }}>
                    Message
                  </Typography>
                  <TextField
                  multiline
                  minRows={3}
                  value={contactMessage}
                  onChange={handleContactMessageChange}
                  variant="filled"
                  fullWidth
                      required
                      disabled={contactSubmitting}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      backgroundColor: '#f5f5f5',
                      borderRadius: 2,
                    },
                  }}
                    placeholder="Tell us what you are looking for..."
                  />
                </Box>
                  <Button 
                    type="submit"
                    variant="contained" 
                    size="large" 
                    disabled={contactSubmitting || !contactEmail.trim() || !contactMessage.trim()}
                    sx={{ borderRadius: 2 }}
                  >
                    {contactSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </Stack>
                </>
              )}
            </Paper>
          )}

          {showPricing && (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 900 },
                p: { xs: 3, md: 4 },
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.32)',
                border: '1px solid rgba(255,255,255,0.45)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(18px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -250 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                  Pricing Plans
                </Typography>
                <IconButton size="small" onClick={() => setShowPricing(false)} sx={{ color: '#0A2540' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                  gap: 2,
                  gridAutoRows: 'minmax(auto, 1fr)',
                }}
              >
                {Object.entries(pricingPlans).map(([key, plan], index) => {
                  // Different blue shades for each pricing plan
                  const blueShades = [
                    { border: 'rgba(33, 150, 243, 0.4)', shadow: 'rgba(33, 150, 243, 0.3)' }, // Light blue - Freemium
                    { border: 'rgba(25, 118, 210, 0.4)', shadow: 'rgba(25, 118, 210, 0.3)' }, // Medium blue - Basic
                    { border: 'rgba(13, 71, 161, 0.4)', shadow: 'rgba(13, 71, 161, 0.3)' }, // Darker blue - Pro
                    { border: 'rgba(4, 55, 123, 0.4)', shadow: 'rgba(4, 55, 123, 0.3)' }, // Darkest blue - Elite
                  ];
                  const blueShade = blueShades[index % blueShades.length];
                  
                  return (
                  <Box
                    key={key}
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      backgroundColor: 'rgba(255,255,255,0.5)',
                      border: `2px solid ${blueShade.border}`,
                      boxShadow: `0 0 15px ${blueShade.shadow}, 0 4px 12px ${blueShade.shadow.replace('0.3', '0.2')}`,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        border: `2px solid ${blueShade.border.replace('0.4', '0.6')}`,
                        boxShadow: `0 0 25px ${blueShade.shadow.replace('0.3', '0.5')}, 0 8px 24px ${blueShade.shadow.replace('0.3', '0.3')}`,
                      },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#0A2540', fontSize: '0.95rem', lineHeight: 1.3 }}>
                      {plan.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#0A2540', fontSize: '0.85rem', lineHeight: 1.4 }}>
                      {plan.price}
                    </Typography>
                    <Box component="ul" sx={{ pl: 3, m: 0, flex: 1 }}>
                      {plan.features.map((feature) => (
                        <Box component="li" key={feature} sx={{ fontSize: '0.85rem', mb: 0.5, color: '#0A2540', lineHeight: 1.4 }}>
                          {feature}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  );
                })}
              </Box>
            </Paper>
          )}

          {showDemo && (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 900 },
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(16px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -250 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.5, color: '#0A2540' }}>
                  Watch Demo
                </Typography>
                <IconButton size="small" onClick={() => setShowDemo(false)} sx={{ color: '#0A2540' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '56.25%', // 16:9 aspect ratio
                  borderRadius: 3,
                  overflow: 'hidden',
                  backgroundColor: '#000',
                }}
              >
                <Box
                  component="iframe"
                  src="https://www.youtube.com/embed/VhRLJtj_FrU?rel=0&modestbranding=1"
                  title="FlowRithm Demo"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            </Paper>
          )}

          {showAboutUs && (
            <Paper
              elevation={0}
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', md: 1000 },
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(16px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -300 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
                maxHeight: { xs: 'auto', md: '85vh' },
                minHeight: { xs: 'auto', md: '600px' },
                overflowY: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#0A2540' }}>
                  About Us
                </Typography>
                <IconButton size="small" onClick={() => setShowAboutUs(false)} sx={{ color: '#0A2540' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <Typography variant="body1" sx={{ color: '#0A2540', mb: 3, lineHeight: 1.7, opacity: 0.9 }}>
                FlowRithm is built by a passionate team of engineers and innovators committed to democratizing 
                enterprise-grade AI tools for traders. We believe in making sophisticated trading intelligence 
                accessible to everyone.
              </Typography>

              <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2540', mb: 3, mt: 3 }}>
                The Team
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 3,
                  mt: 2,
                }}
              >
                {/* Gururaj Bagli */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    border: '2px solid rgba(33, 150, 243, 0.4)',
                    boxShadow: '0 0 15px rgba(33, 150, 243, 0.3), 0 4px 12px rgba(33, 150, 243, 0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: '2px solid rgba(33, 150, 243, 0.6)',
                      boxShadow: '0 0 25px rgba(33, 150, 243, 0.5), 0 8px 24px rgba(33, 150, 243, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      mb: 1.5,
                      mx: 'auto',
                      overflow: 'hidden',
                      border: '3px solid rgba(4, 55, 123, 0.2)',
                      backgroundColor: '#04377b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {!imageErrors['gururaj'] ? (
                      <Box
                        component="img"
                        src="/assets/team/gururaj-bagli.jpg"
                        alt="Gururaj Bagli"
                        onError={() => setImageErrors((prev) => ({ ...prev, gururaj: true }))}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                        GB
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, textAlign: 'center', fontSize: '1rem' }}>
                    Gururaj Bagli
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#04377b', mb: 0.5, textAlign: 'center', fontWeight: 500, fontSize: '0.8rem' }}>
                    Founder & CEO
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <IconButton
                      component="a"
                      href="https://www.linkedin.com/in/gururaj-bagli-5181687/"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: '#0077b5',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 119, 181, 0.1)',
                        },
                      }}
                    >
                      <LinkedInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#0A2540', fontSize: '0.8rem', lineHeight: 1.5, mb: 1.5, opacity: 0.9 }}>
                    Gururaj leads FlowRithm, an Agentic-AI–powered trading intelligence platform. With 18+ years in software quality, benchmarking, and systems engineering, he drives FlowRithm's product vision, Agentic-AI architecture, and quality-first design principles.
                  </Typography>
                </Paper>

                {/* Pallavi S Jeergal */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    border: '2px solid rgba(25, 118, 210, 0.4)',
                    boxShadow: '0 0 15px rgba(25, 118, 210, 0.3), 0 4px 12px rgba(25, 118, 210, 0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: '2px solid rgba(25, 118, 210, 0.6)',
                      boxShadow: '0 0 25px rgba(25, 118, 210, 0.5), 0 8px 24px rgba(25, 118, 210, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      mb: 1.5,
                      mx: 'auto',
                      overflow: 'hidden',
                      border: '3px solid rgba(4, 55, 123, 0.2)',
                      backgroundColor: '#04377b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {!imageErrors['pallavi'] ? (
                      <Box
                        component="img"
                        src="/assets/team/pallavi-jeergal.JPG"
                        alt="Pallavi S Jeergal"
                        onError={() => setImageErrors((prev) => ({ ...prev, pallavi: true }))}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                        PJ
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, textAlign: 'center', fontSize: '1rem' }}>
                    Pallavi S Jeergal
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#04377b', mb: 0.5, textAlign: 'center', fontWeight: 500, fontSize: '0.8rem' }}>
                    HR & Operations
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <IconButton
                      component="a"
                      href="https://www.linkedin.com/in/pallavi-s-jeergal-65076255/"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: '#0077b5',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 119, 181, 0.1)',
                        },
                      }}
                    >
                      <LinkedInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#0A2540', fontSize: '0.8rem', lineHeight: 1.5, mb: 1.5, opacity: 0.9 }}>
                    Pallavi leads HR and Operations at FlowRithm, building the people, processes, and operational foundations required to scale an Agentic-AI platform. She focuses on culture, execution discipline, and quality-driven internal systems that support sustainable product and team growth.
                  </Typography>
                </Paper>

                {/* Raghavendra Prasad */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.5)',
                    border: '2px solid rgba(13, 71, 161, 0.4)',
                    boxShadow: '0 0 15px rgba(13, 71, 161, 0.3), 0 4px 12px rgba(13, 71, 161, 0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      border: '2px solid rgba(13, 71, 161, 0.6)',
                      boxShadow: '0 0 25px rgba(13, 71, 161, 0.5), 0 8px 24px rgba(13, 71, 161, 0.3)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      mb: 1.5,
                      mx: 'auto',
                      overflow: 'hidden',
                      border: '3px solid rgba(4, 55, 123, 0.2)',
                      backgroundColor: '#04377b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    {!imageErrors['raghavendra'] ? (
                      <Box
                        component="img"
                        src="/assets/team/raghavendra-prasad.jpg"
                        alt="Raghavendra Prasad"
                        onError={() => setImageErrors((prev) => ({ ...prev, raghavendra: true }))}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                        RP
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, textAlign: 'center', fontSize: '1rem' }}>
                    Raghavendra Prasad
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#04377b', mb: 0.5, textAlign: 'center', fontWeight: 500, fontSize: '0.8rem' }}>
                    Head of Engineering
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                    <IconButton
                      component="a"
                      href="https://www.linkedin.com/in/raghavbagli/"
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: '#0077b5',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 119, 181, 0.1)',
                        },
                      }}
                    >
                      <LinkedInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" sx={{ color: '#0A2540', fontSize: '0.8rem', lineHeight: 1.5, mb: 1.5, opacity: 0.9 }}>
                    Raghavendra heads engineering at FlowRithm, translating product vision into scalable, cloud-native AI systems. He leads platform architecture, MLOps, and engineering execution to ensure FlowRithm's intelligence platform is robust, secure, and production-ready.
                  </Typography>
                </Paper>
              </Box>
            </Paper>
          )}

          {authMode && (
            <Paper
              elevation={0}
              sx={{
                width: { xs: '100%', md: 420 },
                maxWidth: { xs: 420, md: 420 },
                minWidth: { xs: 'auto', md: 420 },
                p: { xs: 3, md: 5 },
                borderRadius: 4,
                backgroundColor: 'rgba(255,255,255,0.3)',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 20px 60px rgba(10,37,64,0.35)',
                backdropFilter: 'blur(16px)',
                position: { xs: 'relative', md: 'absolute' },
                right: { xs: 'auto', md: -250 },
                top: { xs: 'auto', md: '50%' },
                transform: { xs: 'none', md: 'translateY(-50%)' },
                zIndex: 10,
              }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: 0.5, color: '#0A2540' }}>
                    {authMode === 'signup' ? 'Create an account' : 'Login to FlowRithm'}
                  </Typography>
                  <IconButton size="small" onClick={() => setAuthMode(null)} sx={{ color: '#0A2540' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>

                <Typography variant="body2" sx={{ color: '#0A2540', mb: 4 }}>
                  {authMode === 'signup'
                    ? 'Fill in your details to start using FlowRithm.'
                    : 'Use your registered credentials or single sign-on options to continue.'}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <Stack component="form" onSubmit={handleSubmit} spacing={2.5}>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, display: 'block' }}>
                      Mobile Number
                    </Typography>
                    <TextField
                    type="tel"
                    value={mobileNumber}
                    onChange={handleMobileChange}
                    variant="filled"
                    fullWidth
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        fontWeight: 500,
                        '& input': { py: 1.5 },
                      },
                    }}
                      placeholder="Enter mobile number"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, display: 'block' }}>
                      Email ID *
                    </Typography>
                    <TextField
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    fullWidth
                    required
                    autoComplete="email"
                    autoFocus
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        fontWeight: 500,
                        '& input': { py: 1.5 },
                      },
                    }}
                      placeholder="name@domain.com"
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.5, display: 'block' }}>
                      Password *
                    </Typography>
                    <TextField
                    type="password"
                    value={password}
                    onChange={handlePasswordChange}
                    fullWidth
                    required
                    autoComplete="current-password"
                    variant="filled"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        backgroundColor: '#f0f0f0',
                        borderRadius: 2,
                        fontWeight: 500,
                        '& input': { py: 1.5 },
                      },
                    }}
                      placeholder="Enter password"
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Checkbox checked={rememberMe} onChange={handleRememberMeChange} color="primary" />
                    }
                    label="Remember me"
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    size="large"
                    sx={{ borderRadius: 2 }}
                  >
                    {loading ? 'Processing...' : authMode === 'signup' ? 'Sign Up' : 'Sign In'}
                  </Button>
                </Stack>

                <Divider sx={{ my: 3 }}>or</Divider>

                <Button
                  variant="contained"
                  color="inherit"
                  fullWidth
                  size="large"
                  disabled={loading}
                  onClick={async () => {
                    try {
                      await signInWithGoogle();
                      navigate('/ai-assist');
                    } catch (err) {
                      // Error handled by useAuth hook
                    }
                  }}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.85)',
                    color: '#212121',
                    borderRadius: 2,
                    border: '1px solid rgba(0,0,0,0.1)',
                    textTransform: 'none',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1.5,
                    '&:hover': { 
                      backgroundColor: 'rgba(255,255,255,0.95)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(255,255,255,0.6)',
                    },
                  }}
                  startIcon={
                    <Box
                      component="img"
                      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%234285F4' d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'/%3E%3Cpath fill='%2334A853' d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'/%3E%3Cpath fill='%23FBBC05' d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'/%3E%3Cpath fill='%23EA4335' d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'/%3E%3C/svg%3E"
                      alt="Google"
                      sx={{ width: 20, height: 20 }}
                    />
                  }
                >
                  {loading ? 'Signing in...' : 'Continue with Google'}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 3 }}>
                  <Button
                    variant="text"
                    onClick={handleForgotPassword}
                    sx={{ textTransform: 'none', padding: 0, mb: 1 }}
                  >
                    <Typography variant="body2" color="primary">
                      Forgot password?
                    </Typography>
                  </Button>
                  <Typography variant="body2">
                    {authMode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                    <Button
                      size="small"
                      variant="text"
                    onClick={() => handleAuthOpen(authMode === 'signup' ? 'login' : 'signup')}
                      sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                    >
                      {authMode === 'signup' ? 'Sign in' : 'Sign up'}
                    </Button>
                  </Typography>
                </Box>
              </Paper>
          )}
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          background: 'linear-gradient(180deg, #fff8ef 0%, #f5f0e8 100%)',
          color: '#04377b',
          mt: { xs: 4, md: 6 },
          px: { xs: 3, md: 5 },
          py: { xs: 1.9, md: 2 },
          border: '1px solid',
          borderColor: 'rgba(4, 55, 123, 0.3)',
          borderBottom: 'none',
          borderRadius: '8px 8px 0 0',
          boxShadow: `
            0 0 0 1px rgba(4, 55, 123, 0.15),
            0 0 8px rgba(4, 55, 123, 0.2),
            0 0 15px rgba(4, 55, 123, 0.15),
            0 -3px 16px rgba(4, 55, 123, 0.25),
            0 -1px 6px rgba(4, 55, 123, 0.15),
            inset 0 0 10px rgba(255, 255, 255, 0.03),
            inset 0 1px 0 rgba(255, 255, 255, 0.1),
            inset 0 -1px 0 rgba(4, 55, 123, 0.1)
          `,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                component="img"
                src="/assets/flowrithm-logo.png"
                alt="flowrithm"
                sx={{
                  height: { xs: 24, sm: 28 },
                  width: 'auto',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.8)' }}>
              Unified Broker Access | Agentic AI Workflows | Pro-Grade Security
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: 'rgba(4, 55, 123, 0.8)' }}>
            contact@flowrithm.trade | +91 98866 44123
          </Typography>
        </Box>

        <Box
          sx={{
            mt: 1.25,
            pt: 1,
            borderTop: '1px solid rgba(4, 55, 123, 0.2)',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            fontSize: '0.75rem',
            color: 'rgba(4, 55, 123, 0.75)',
          }}
        >
          <Typography variant="body2">© {currentYear} flowRithm · Built in Bengaluru.</Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              component={Link}
              to="/policy/cookie"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Cookie Policy
            </Button>
            <Button
              component={Link}
              to="/policy/disclaimer"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Disclaimer
            </Button>
            <Button
              component={Link}
              to="/policy/privacy"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Privacy Policy
            </Button>
            <Button
              component={Link}
              to="/policy/disclosure"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Disclosure Agreement
            </Button>
            <Button
              component={Link}
              to="/policy/terms"
              variant="text"
              sx={{
                color: 'rgba(4, 55, 123, 0.75)',
                textTransform: 'none',
                p: 0,
                minWidth: 0,
                fontSize: '0.8rem',
                '&:hover': { color: '#04377b' },
              }}
            >
              Terms of Use
            </Button>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

